import { useCallback, useContext, useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { diContainerContext } from 'application/contexts/useDiContainer';
import { ResponseMeetingRequestRepository } from 'interfaceAdapter/repositories/ResponseMeetingRequest';
import { CompanyUserQuery } from 'interfaceAdapter/queries/CompanyUser';
import { companyUserContext } from 'application/contexts/useCompanyUser';
import yup from 'utils/yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useModal } from 'ui/components/screens/modals/useModal';
import { IMatch } from 'domain/entities/Match/Match';
import { useLoading } from 'application/contexts/useLoading';
import { useNavigate } from 'react-router-dom';

const schema = yup
    .object({
        reason: yup.string().required().max(1000),
        match_id: yup.number().required(),
    })
    .required();

type DeclineForm = yup.InferType<typeof schema>;

export const useRequestMeetList = () => {
    const diContainer = useContext(diContainerContext);
    const meetingRequestRepository = diContainer.resolve(ResponseMeetingRequestRepository);
    const companyUserQuery = diContainer.resolve(CompanyUserQuery);
    const { user, setUser } = useContext(companyUserContext);
    const { loading, setLoading } = useLoading();
    const [showSnackbar, setShowSnackbar] = useState(false);
    const [showSnackbarMessage, setShowSnackbarMessage] = useState('');
    const [premiumOffers, setPremiumOffers] = useState<IMatch[]>([]);
    const declineModal = useModal();
    const { openModal, createModal, closeModal } = declineModal;
    const navigate = useNavigate();

    const {
        handleSubmit,
        formState: { errors },
        setValue,
    } = useForm<DeclineForm>({
        resolver: yupResolver(schema),
        mode: 'onChange',
        defaultValues: {
            reason: '',
        },
    });

    useEffect(() => {
        if (user?.waiting_request_meeting) {
            const premiumOffers = user.waiting_request_meeting.filter(
                (request_meeting) => request_meeting.premium_offer,
            );
            setPremiumOffers(premiumOffers);
        }
    }, [user]);

    const refetchUser = useCallback(async () => {
        const { user, status } = await companyUserQuery.checkToken(true);
        if (status !== false) {
            setUser(user);
        }
    }, []);

    const accept = useCallback(async (match_id: number) => {
        setLoading(true);
        try {
            await meetingRequestRepository.accepted(match_id);
            await refetchUser(); // 面談情報を再取得するため
            setShowSnackbarMessage('オファーを承認しました');
            setShowSnackbar(true);
            setTimeout(() => {
                setShowSnackbar(false);
                setShowSnackbarMessage('');
                navigate(`/company/message/`);
            }, 2000);
        } finally {
            setLoading(false);
        }
    }, []);

    const decline = handleSubmit(async ({ match_id, reason }) => {
        setLoading(true);
        try {
            await meetingRequestRepository.declined(match_id, reason);
            await refetchUser(); // 面談情報を再取得するため
            setShowSnackbarMessage('オファーをお断りしました');
            setShowSnackbar(true);
            closeModal();
            setTimeout(() => {
                setShowSnackbar(false);
                setShowSnackbarMessage('');
            }, 2000);
        } finally {
            setLoading(false);
        }
    });

    return {
        accept,
        decline,
        loading,
        user,
        premiumOffers,
        showSnackbarMessage,
        showSnackbar,
        setShowSnackbar,
        setValue,
        errors,
        declineModal,
        openModal,
        createModal,
    };
};
